import type { MailboxInfo } from 'owa-client-types';
import { getMailboxTypeForBizchatTelemetry } from 'owa-bizchat-account-utils';
import { OKRCategories } from './types/OKRCategories';
import { logCoreUsage } from 'owa-analytics';

export type InboxBriefingsTelemetryEvent =
    | 'InboxBriefingsTriggered' // User initiates an Inbox Briefing via the nudge or SUA
    | 'InboxBriefingsNudgeRendered' // Nudge impression
    | 'InboxBriefingsNudgeUserDismissed' // User explicitly dismisses the nudge
    | 'InboxBriefingsNudgeAutoDismissed' // Nudge auto-dismissed because the briefing window expired
    | 'InboxBriefingsTierChange'; // User moved between engagement tiers

type InboxBriefingsTelemetryEntryPoint = 'MessageList' | 'SUA';

const INBOX_BRIEFINGS_SCENARIO = 'InboxBriefings';

function getOKRCategoryForEvent(event: InboxBriefingsTelemetryEvent): OKRCategories {
    switch (event) {
        case 'InboxBriefingsNudgeRendered':
            return OKRCategories.Seen;
        case 'InboxBriefingsTriggered':
            return OKRCategories.TriedInitiate;
        case 'InboxBriefingsNudgeUserDismissed':
            // Closed (distinct string value from None / Discard) is used so user-initiated
            // dismissals are distinguishable in OKR pipelines.
            return OKRCategories.Closed;
        case 'InboxBriefingsNudgeAutoDismissed':
        case 'InboxBriefingsTierChange':
        default:
            return OKRCategories.None;
    }
}

function getEntryPointForEvent(
    event: InboxBriefingsTelemetryEvent,
    customData: {
        [key: string]: any;
    }
): InboxBriefingsTelemetryEntryPoint {
    switch (event) {
        case 'InboxBriefingsTriggered':
            // Triggered fires from either the nudge (MessageList) or the SUA tile.
            return customData.source === 'SUA' ? 'SUA' : 'MessageList';
        case 'InboxBriefingsNudgeRendered':
        case 'InboxBriefingsNudgeUserDismissed':
        case 'InboxBriefingsNudgeAutoDismissed':
        case 'InboxBriefingsTierChange':
        default:
            return 'MessageList';
    }
}

function getActionForEvent(event: InboxBriefingsTelemetryEvent): string {
    switch (event) {
        case 'InboxBriefingsNudgeRendered':
            return 'Seen';
        case 'InboxBriefingsTriggered':
            return 'Generate';
        case 'InboxBriefingsNudgeUserDismissed':
            return 'UserDismiss';
        case 'InboxBriefingsNudgeAutoDismissed':
            return 'AutoDismiss';
        case 'InboxBriefingsTierChange':
            return 'TierChange';
        default:
            return 'Other';
    }
}

/**
 * Standalone logger for Inbox Briefings telemetry events. Emits the standardized
 * AIStandardTelemetry scorecard event so Inbox Briefings appears in the Copilot
 * OKR pipeline alongside other Copilot scenarios.
 *
 * Follows the prioritizeTelemetryLogger pattern (no CopilotTelemetrySession
 * because there is no multi-step LLM round-trip — the nudge/SUA simply opens
 * BizChat where its own telemetry takes over).
 */
export function logInboxBriefingsTelemetryEvent(
    event: InboxBriefingsTelemetryEvent,
    mailboxInfo: MailboxInfo,
    customData: {
        [key: string]: any;
    } = {}
): void {
    logCoreUsage(
        'AIStandardTelemetry',
        mailboxInfo,
        {
            CopilotEventName: event,
            OKRCategory: getOKRCategoryForEvent(event),
            EntryPoint: getEntryPointForEvent(event, customData),
            Action: getActionForEvent(event),
            Scenario: INBOX_BRIEFINGS_SCENARIO,
            MailboxProvider: mailboxInfo.mailboxProvider,
            MailboxType: getMailboxTypeForBizchatTelemetry(mailboxInfo),
            ...customData,
        },
        { isScorecardEvent: true }
    );
}
